import {TID} from "@atproto/common-web";
import {useMutation} from "@tanstack/react-query";
import {useATProtoAgent} from "@/app/atproto";
import {logger} from "@/app/events";
import {
  createLibraryEntry,
  deleteLibraryEntry,
  updateLibraryEntry,
} from "@/app/library/api";
import {db} from "@/app/storage";
import {IDS, type LibraryEntry} from "@/shared/types";

export function useCreateLibraryEntry() {
  const agent = useATProtoAgent();

  return useMutation({
    mutationFn: async (entry: Omit<LibraryEntry, "createdAt" | "updatedAt">) => {
      const rkey = TID.nextStr();
      const uri = `at://${agent.did}/${IDS.LibraryEntry}/${rkey}`;
      const now = new Date().toISOString();
      const newEntry = {
        ...entry,
        createdAt: now,
        updatedAt: now,
      };

      // 1. Optimistic local update
      await db.libraryEntries.put({
        ...newEntry,
        uri,
        cid: "pending",
        synced: false,
        isDeleted: 0,
      });

      try {
        // 2. Sync to PDS
        const pdsResult = await createLibraryEntry(agent, entry, rkey);

        // 3. Update local with confirmation
        await db.libraryEntries.update(uri, {
          cid: pdsResult.cid,
          synced: true,
        });
        return pdsResult;
      } catch (error) {
        // Keep synced: false to indicate retry needed later
        logger.storage.error("Failed to sync create to PDS", {error});
        throw error;
      }
    },
  });
}

export function useUpdateLibraryEntry() {
  const agent = useATProtoAgent();

  return useMutation({
    mutationFn: async (target: {
      uri: string;
      cid: string;
      entry: Omit<LibraryEntry, "updatedAt">;
    }) => {
      const {uri, cid, entry} = target;

      // 1. Optimistic local update
      await db.libraryEntries.update(uri, {
        ...entry,
        updatedAt: new Date().toISOString(),
        synced: false,
        isDeleted: 0,
      });

      try {
        // 2. Sync to PDS
        const pdsResult = await updateLibraryEntry(agent, uri, cid, entry);

        // 3. Update local with confirmation
        await db.libraryEntries.update(uri, {
          cid: pdsResult.cid,
          synced: true,
        });
        return pdsResult;
      } catch (error) {
        console.error("Failed to sync update to PDS", error);
        throw error;
      }
    },
  });
}

export function useDeleteLibraryEntry() {
  const agent = useATProtoAgent();

  return useMutation({
    mutationFn: async (uri: string) => {
      // 1. Optimistic local update (soft delete)
      await db.libraryEntries.update(uri, {
        isDeleted: 1, // Redundant but harmless, fixing indentation
        synced: false, // Redundant
      });
      // Actually, wait, let me fix the indentation properly.
      await db.libraryEntries.update(uri, {
        isDeleted: 1,
        synced: false,
      });

      try {
        // 2. Sync to PDS
        await deleteLibraryEntry(agent, uri);

        // 3. Hard delete locally after success
        // In a real robust system, we might keep it as a tombstone until confirmed sync across devices
        // But for this MVP, deleting it locally after sync is fine.
        await db.libraryEntries.delete(uri);
      } catch (error) {
        console.error("Failed to sync delete to PDS", error);
        throw error;
      }
    },
  });
}
